import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postDeliveryOrder, type DeliveryOrderCreateBody } from '@apis/logistics';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { DeliveryOrderDialog } from '../../../LogisticsPage/panels/DeliveryOrderDialog';
import { FormDialogShell } from '../../../LogisticsPage/panels/FormDialogShell';
import { useLogisticsQueries } from '../../../LogisticsPage/useLogisticsQueries';
import { materialPrefillFor, openOrderNoticeFor, stockUnitFor } from '../../../LogisticsPage/orderDialogDerivations';
import { originNoteFor, type IOrderHandoff } from '../../../LogisticsPage/orderHandoff';
import { fold } from '../../../LogisticsPage/units';
import { fireThenPersist } from '../../stock/persistAfterWrite';
import { useJoinWrites, type IJoinFailure } from '../../stock/useJoinWrites';
import styles from './StockDeliveryDialog.module.css';
import { OWNS_ERROR_FEEDBACK, logisticsKeys } from '@d-orca/shared/client/hooks';
import { supplierSaveErrorOf } from '@d-orca/shared/client/logistics';

interface IStockDeliveryDialogProps {
  siteId: string;
  handoff: IOrderHandoff;
  /** Runs a tick after the order is posted (`fireThenPersist`) — the drawer
   *  persists its draft here, so the delivery never points at a claim that was
   *  never saved, and a cancelled dialog leaves nothing behind. */
  onBeforeCreate: () => void;
  onClose: () => void;
}

/**
 * The Stock tab checkout's order details: the Logistics delivery dialog,
 * opened right here above the task drawer rather than a page away. Same form,
 * same lookups (`orderDialogDerivations`), same write — the differences are
 * that this host only ever CREATES, seeds one row per material the checkout
 * orders, stamps each of those lines with the task it was raised for, says
 * above Create which adds to orders already on their way Create also makes
 * (and makes them once the order exists), has the drawer persist its draft on
 * Create (`onBeforeCreate`), and reports a failure inside the dialog (the
 * drawer has no toast stack of its own).
 *
 * Renders nothing until the site's logistics payload and timezone are in:
 * the Stock tab already waited for both before offering the checkout, so in
 * practice that is never visible.
 */
export function StockDeliveryDialog({ siteId, handoff, onBeforeCreate, onClose }: IStockDeliveryDialogProps) {
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz);
  const { logisticsQuery, statusesQuery, suppliersQuery } = useLogisticsQueries(siteId);
  const joins = useJoinWrites(siteId);
  const queryClient = useQueryClient();
  // The adds refused AFTER the order was created. The order stands, so the
  // dialog turns into the report of what did not land — the material stays
  // on the task's list, on its way, for the review to try again.
  const [failed, setFailed] = useState<IJoinFailure[]>([]);
  const createOrder = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (body: DeliveryOrderCreateBody) => postDeliveryOrder(siteId, body, null),
    // Once the order exists, the planned adds run in order; their re-read of
    // the site payload is also the one the new order needs.
    onSuccess: async () => {
      // The order may have saved a new or edited supplier with it.
      void queryClient.invalidateQueries({ queryKey: logisticsKeys.suppliers(siteId) });
      const failures = await joins.run(handoff.joins);
      if (failures.length === 0) onClose();
      else setFailed(failures);
    },
  });

  const data = logisticsQuery.data;
  if (!data || !tz) return null;

  if (failed.length > 0) {
    return (
      <FormDialogShell
        onClose={onClose}
        title={t('logistics.orderDialog.titleCreate')}
        cancelLabel={t('scheduler.panel.stock.cart.close')}
        classes={styles}
      >
        {(close) => (
          <>
            {failed.map((failure) => (
              <p key={failure.claimId} role="alert" className={styles.error}>
                {t('scheduler.panel.stock.cart.joinFailed', { ref: failure.ref })}
              </p>
            ))}
            <div className={styles.footer}>{close}</div>
          </>
        )}
      </FormDialogShell>
    );
  }

  return (
    <DeliveryOrderDialog
      suppliers={suppliersQuery.data ?? []}
      saveError={createOrder.error}
      tz={tz}
      suggestions={data.materialSuggestions}
      materialPrefill={materialPrefillFor(data, tz, t)}
      stockUnitOf={stockUnitFor(data.inventory)}
      deliveries={data.deliveries}
      openOrderNotice={openOrderNoticeFor(data.deliveries)}
      initial={null}
      boardStatuses={statusesQuery.data ?? []}
      prefills={handoff.lines.map((line) => line.prefill)}
      originNote={originNoteFor(handoff, t)}
      // A hand-off's unit is the CLAIM's unit — a starting guess, not a fact
      // of a known material the way an inventory pick's unit is.
      unitEditable
      footerNote={handoff.joins
        .map((write) =>
          t('scheduler.panel.stock.cart.alsoAdds', {
            qty: write.body.quantity,
            unit: write.body.unit,
            material: write.body.itemName,
            ref: write.ref,
          }),
        )
        .join(' ')}
      onSubmit={(body) => {
        // No column key exists on a create — the dialog offers no column
        // there; `postDeliveryOrder` types it as `never`.
        const { status: _status, ...create } = body;
        // Each line the checkout raised carries the task's source, matched by
        // material: a line the user added (or renamed) by hand gets none, and
        // a removed one takes its source with it.
        const lines = create.lines.map((line) => {
          const from = handoff.lines.find((h) => fold(h.prefill.itemName) === fold(line.itemName));
          return from === undefined ? line : { ...line, source: from.source };
        });
        fireThenPersist(() => createOrder.mutate({ ...create, lines }), onBeforeCreate);
      }}
      onClose={onClose}
      submitting={createOrder.isPending}
      // A refused supplier draft shows on the supplier fields instead.
      errorMessage={createOrder.isError && supplierSaveErrorOf(createOrder.error) === null ? t('logistics.pipeline.saveError') : undefined}
    />
  );
}
